import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del } from '@utils/axios_api';
import { toast } from 'react-toastify';

const QUERY_KEY = ['roleList'];

/**
 * 역할 CRUD 훅 (슈퍼관리자 전용)
 * - 전체 역할 목록 동적 조회 (시스템 + 커스텀)
 * - 커스텀 역할 생성 / 표시명 수정 / 삭제
 */
const useRoleManagement = () => {
    const queryClient = useQueryClient();

    // ── 전체 역할 목록 ──────────────────────────────────────────────────
    const { data: roleList = [], isLoading } = useQuery({
        queryKey: QUERY_KEY,
        queryFn: () => get('/super/comm/role/list').then(res => res?.result ?? []),
        staleTime: 5 * 60 * 1000,
    });

    const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

    // ── 역할 생성 ────────────────────────────────────────────────────────
    const createRoleMutation = useMutation({
        mutationFn: (dto) => post('/super/comm/role', dto),
        onSuccess: () => {
            toast.success('역할이 생성되었습니다.');
            invalidate();
        },
        onError: (err) => {
            const msg = err?.response?.data?.message ?? '역할 생성에 실패했습니다.';
            toast.error(msg);
        },
    });

    // ── 역할 표시명 수정 ─────────────────────────────────────────────────
    const updateRoleMutation = useMutation({
        mutationFn: ({ roleCode, displayName }) =>
            put(`/super/comm/role/${roleCode}`, { displayName }),
        onSuccess: () => {
            toast.success('역할이 수정되었습니다.');
            invalidate();
        },
        onError: () => toast.error('역할 수정에 실패했습니다.'),
    });

    // ── 역할 삭제 ────────────────────────────────────────────────────────
    const deleteRoleMutation = useMutation({
        mutationFn: (roleCode) => del(`/super/comm/role/${roleCode}`),
        onSuccess: () => {
            toast.success('역할이 삭제되었습니다.');
            invalidate();
        },
        onError: (err) => {
            const msg = err?.response?.data?.message ?? '역할 삭제에 실패했습니다.';
            toast.error(msg);
        },
    });

    return {
        roleList,
        isLoading,
        createRole: (dto) => createRoleMutation.mutate(dto),
        updateRole: (roleCode, displayName) => updateRoleMutation.mutate({ roleCode, displayName }),
        deleteRole: (roleCode) => deleteRoleMutation.mutate(roleCode),
        isCreating: createRoleMutation.isPending,
        isDeleting: deleteRoleMutation.isPending,
    };
};

export default useRoleManagement;
